import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Pagination from "@mui/material/Pagination";
import { FiCalendar, FiArrowRight, FiChevronsRight } from "react-icons/fi";
import {
  GiMicroscope,
  GiChemicalDrop,
  GiArchiveResearch,
  GiBookshelf,
  GiGraduateCap,
  GiLightBulb,
} from "react-icons/gi";
import s from "./IlmiyMaktablar.module.css";
import { testUrl } from "../BaseData";

// Sahifa matnlari (uz / en / ru)
const TEXT = {
  uz: {
    home: "Bosh sahifa",
    title: "Ilmiy maktablar",
    heroText:
      "Ta'lim darslik va auditoriyadan kengroq. Biz talabalar va yosh olimlarni o'z qiziqishlarini chuqur o'rganishga, yangi g'oyalar yaratishga undaymiz.",
    highlights: "Ilmiy maktablar faoliyati",
    highlightsText:
      "Universitet bakalavriatdan tortib doktorantura darajasigacha bo'lgan barcha bosqichlarda ilmiy izlanishlarni qo'llab-quvvatlaydi. Maqolalar, konferensiyalar va xalqaro hamkorliklar orqali olimlarimiz jahon ilm-faniga hissa qo'shmoqda.",
    more: "Batafsil",
    about: "Ilmiy maktablar haqida",
    sections: "Ilmiy faoliyat yo'nalishlari",
    sectionText: "Bo'limga o'tish uchun bosing",
    empty: "Hozircha ma'lumot mavjud emas",
    loading: "Yuklanmoqda...",
    error: "Ma'lumotlarni yuklashda xatolik",
    latest: "So'nggi material",
  },
  en: {
    home: "Home",
    title: "Scientific Schools",
    heroText:
      "Education goes beyond textbooks and classrooms. We empower students and young researchers to explore their passions and create new ideas.",
    highlights: "Scientific Schools Highlights",
    highlightsText:
      "We support research at every level, from undergraduate exploration to doctoral inquiry. Through publications, conferences and global collaborations, our researchers contribute to international science.",
    more: "Read more",
    about: "About Scientific Schools",
    sections: "Research Focus Areas",
    sectionText: "Click to open the section",
    empty: "No materials yet",
    loading: "Loading...",
    error: "Failed to load data",
    latest: "Latest material",
  },
  ru: {
    home: "Главная",
    title: "Научные школы",
    heroText:
      "Образование выходит за рамки учебников и аудиторий. Мы помогаем студентам и молодым учёным развивать свои интересы и создавать новые идеи.",
    highlights: "Деятельность научных школ",
    highlightsText:
      "Университет поддерживает научные исследования на всех уровнях — от бакалавриата до докторантуры. Через публикации, конференции и международное сотрудничество наши учёные вносят вклад в мировую науку.",
    more: "Подробнее",
    about: "О научных школах",
    sections: "Направления научной деятельности",
    sectionText: "Нажмите, чтобы перейти в раздел",
    empty: "Пока нет материалов",
    loading: "Загрузка...",
    error: "Ошибка загрузки данных",
    latest: "Последний материал",
  },
};

const ICONS = [GiMicroscope, GiChemicalDrop, GiArchiveResearch, GiBookshelf, GiGraduateCap, GiLightBulb];

const NAVBAR_ORDER = [
  "ilmiy-jarayonlar",
  "ilmiy-kengash",
  "ilmiy-konferensiyalar",
  "journal",
  "ilmiy-maktablar",
  "green-renessans",
  "oquv-va-labaratoriya-bazalari",
  "axborotlashtirish-jarayonlari",
  "yosh-akademiklar",
  "grantlar-va-loyihalar",
  "avtorefereat-dissertatsiya",
];

const sortSidebarItems = (items) =>
  [...items].sort((a, b) => {
    const ai = NAVBAR_ORDER.indexOf(a.short_name);
    const bi = NAVBAR_ORDER.indexOf(b.short_name);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return 0;
  });

const LOCALES = { uz: "uz-Latn-UZ", en: "en-US", ru: "ru-RU" };

const formatDate = (value, lang) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return "";
  if (lang === "uz") {
    // Brauzerlar o'zbekcha oy nomlarini to'g'ri chiqarmaydi — qo'lda
    const oy = ["yanvar", "fevral", "mart", "aprel", "may", "iyun", "iyul", "avgust", "sentyabr", "oktyabr", "noyabr", "dekabr"];
    return `${d.getDate()}-${oy[d.getMonth()]}, ${d.getFullYear()}`;
  }
  try {
    return d.toLocaleDateString(LOCALES[lang] || "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return d.toLocaleDateString();
  }
};

const IlmiyMaktablar = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = TEXT[i18n.language] ? i18n.language : "uz";
  const T = TEXT[lang];

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityTypes, setActivityTypes] = useState([]);
  const [news, setNews] = useState([]);
  const [page, setPage] = useState(1);
  const itemsPerPage = 6;
  const currentShort = "ilmiy-maktablar";

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get(testUrl + "/api/baseactivity/ilmiy-faoliyat/ilmiy-maktablar")
      .then(({ data }) => {
        if (!data) return;
        setCategory(data.category || null);
        setActivity(data.activity || null);
        setActivityTypes(sortSidebarItems(data.activities_types || []));
        setNews((data.activities_post || []).filter(Boolean));
      })
      .catch(() => setError(T.error))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const name = (obj) => (obj ? obj[`name_${lang}`] || obj.name_uz : "");

  const pageCount = Math.ceil(news.length / itemsPerPage);
  const currentNews = news.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const featured = news[0];
  const heroImage = useMemo(() => news.find((n) => n.image)?.image, [news]);
  const aboutHtml = activity ? activity[`site_details_${lang}`] || activity.site_details_uz : "";
  const otherTypes = activityTypes.filter((c) => c.short_name !== currentShort);

  const handlePage = (_, value) => {
    setPage(value);
    document.getElementById("im-list")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className={s.page}>
      {/* ===== HERO ===== */}
      <section
        className={s.hero}
        style={heroImage ? { backgroundImage: `url(${heroImage})` } : undefined}
      >
        <div className={s.heroOverlay} />
        <div className={s.heroInner}>
          <nav className={s.breadcrumb}>
            <Link to="/">{T.home}</Link>
            <FiChevronsRight />
            {category && <span className={s.crumbMuted}>{name(category)}</span>}
            {category && <FiChevronsRight />}
            <span>{name(activity) || T.title}</span>
          </nav>
          <h1 className={s.heroTitle}>{name(activity) || T.title}</h1>
          <div className={s.heroLine}>
            <span />
          </div>
          <p className={s.heroText}>{T.heroText}</p>
        </div>
      </section>

      <div className={s.container}>
        {loading ? (
          <div className={s.state}>{T.loading}</div>
        ) : error ? (
          <div className={s.state}>{error}</div>
        ) : (
          <>
            {/* ===== HIGHLIGHTS LIST ===== */}
            <section id="im-list" className={s.section} data-aos="fade-up">
              <h2 className={s.sectionTitle}>{T.highlights}</h2>
              <p className={s.sectionLead}>{T.highlightsText}</p>

              {currentNews.length === 0 ? (
                <div className={s.state}>{T.empty}</div>
              ) : (
                <div className={s.list}>
                  {currentNews.map((item) => (
                    <article
                      key={`${item.id}-${item.created_at}`}
                      className={s.row}
                      onClick={() => navigate(`/activity/${item.id}`)}
                    >
                      <div className={s.rowText}>
                        {item.created_at && (
                          <div className={s.date}>
                            <FiCalendar />
                            {formatDate(item.created_at, lang)}
                          </div>
                        )}
                        <h3 className={s.rowTitle}>{name(item)}</h3>
                        <div className={s.rowMeta}>{name(activity) || T.title}</div>
                      </div>
                      <div className={s.rowAction}>
                        <button
                          type="button"
                          className={s.btn}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/activity/${item.id}`);
                          }}
                        >
                          {T.more} <FiArrowRight />
                        </button>
                      </div>
                      <div className={s.rowImage}>
                        {item.image ? (
                          <img src={item.image} alt={name(item)} loading="lazy" />
                        ) : (
                          <div className={s.imgPlaceholder}>
                            <GiMicroscope />
                          </div>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}

              {pageCount > 1 && (
                <div className={s.pagination}>
                  <Pagination
                    onChange={handlePage}
                    count={pageCount}
                    page={page}
                    variant="outlined"
                    shape="rounded"
                  />
                </div>
              )}
            </section>

            {/* ===== FEATURED WIDE IMAGE ===== */}
            {featured?.image && (
              <section
                className={s.featured}
                data-aos="fade-up"
                onClick={() => navigate(`/activity/${featured.id}`)}
              >
                <img src={featured.image} alt={name(featured)} />
                <div className={s.featuredOverlay}>
                  <span className={s.featuredTag}>{T.latest}</span>
                  <h3>{name(featured)}</h3>
                  <span className={s.featuredLink}>
                    {T.more} <FiArrowRight />
                  </span>
                </div>
              </section>
            )}

            {/* ===== ABOUT (admin rich text) ===== */}
            {aboutHtml && (
              <section className={s.section} data-aos="fade-up">
                <h2 className={s.sectionTitle}>{T.about}</h2>
                <div
                  className={`${s.aboutBox} ck-content`}
                  dangerouslySetInnerHTML={{ __html: aboutHtml }}
                />
              </section>
            )}

            {/* ===== FOCUS AREAS (sidebar o'rniga) ===== */}
            {otherTypes.length > 0 && (
              <section className={s.section} data-aos="fade-up">
                <h2 className={s.sectionTitle}>{name(category) || T.sections}</h2>
                <div className={s.cards}>
                  {otherTypes.map((cat, i) => {
                    const Icon = ICONS[i % ICONS.length];
                    return (
                      <div
                        key={cat.id}
                        className={s.card}
                        onClick={() =>
                          navigate(`/activity/${category?.short_name || "ilmiy-faoliyat"}/${cat.short_name}`)
                        }
                      >
                        <div className={s.cardIcon}>
                          <Icon />
                        </div>
                        <h4>{name(cat)}</h4>
                        <p>{T.sectionText}</p>
                        <span className={s.cardArrow}>
                          <FiArrowRight />
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default IlmiyMaktablar;
